/**
 * Static catalog for the Data Classification report section.
 *
 * Per-data-type description and a "required security controls" set are
 * authored here so the Reviewer Console can stay focused on
 * classification + volume + notes, while the report still renders a
 * professional, consistent matrix.
 *
 * Business Impact is composed at render time from
 *   classification × volume × telecom context.
 */

export type Sensitivity = "Low" | "Moderate" | "High" | "Critical";
export type Volume = "Small" | "Moderate" | "Large" | "Very Large";

export interface DataTypeCatalogEntry {
  description: string;
  /** Short, executive-friendly control set (3–6 items). */
  requiredControls: string[];
  /** Optional regulatory drivers used to enrich the business impact line. */
  drivers?: string[];
}

export const DATA_TYPE_CATALOG: Record<string, DataTypeCatalogEntry> = {
  "Personally Identifiable Information (PII)": {
    description:
      "Subscriber identity attributes (names, identifiers, contact details, government IDs) collected during onboarding and service delivery.",
    requiredControls: [
      "Data minimisation & retention schedule",
      "Encryption at rest and in transit",
      "Role-based access with MFA",
      "Privacy notice & lawful basis tracking",
      "Subject-access / erasure workflow",
    ],
    drivers: ["Privacy"],
  },
  "Customer Account Data": {
    description:
      "Service plans, account history, account credentials and customer support records associated with active subscribers.",
    requiredControls: [
      "Encryption at rest and in transit",
      "Least-privilege access for CSR/CRM systems",
      "Session controls & audit logging",
      "Backups with tested recovery",
    ],
    drivers: ["Privacy", "Operational"],
  },
  "Subscriber Metadata / Call Detail Records": {
    description:
      "Call detail records (CDRs), session metadata, location and connection records produced by core network elements.",
    requiredControls: [
      "Strong access controls + privileged-access logging",
      "Encryption at rest and in transit",
      "Retention policy aligned to regulator",
      "Tamper-evident audit trails",
      "Separation of duties for mediation/billing",
    ],
    drivers: ["Privacy", "Regulatory"],
  },
  "Network Traffic Data": {
    description:
      "Live and captured packet, flow and protocol-level data crossing transport, core and peering networks.",
    requiredControls: [
      "Network segmentation",
      "Encrypted management plane",
      "DDoS protection & traffic monitoring",
      "Just-in-time access to inspection tools",
    ],
    drivers: ["Operational", "Security"],
  },
  "Authentication Credentials / Identity Data": {
    description:
      "Subscriber and workforce credentials, including SIM/USIM keys, IMSI/Ki material, and admin identity stores.",
    requiredControls: [
      "Phishing-resistant MFA",
      "Hardware-backed key storage (HSM)",
      "Credential rotation & secrets vaulting",
      "Privileged access workstation enforcement",
      "Continuous identity threat detection",
    ],
    drivers: ["Security", "Regulatory"],
  },
  "Billing and Financial Records": {
    description:
      "Invoicing, charging, settlement and revenue assurance data flowing through OSS/BSS and finance systems.",
    requiredControls: [
      "Segregation of duties",
      "Change control on billing rules",
      "Encryption & tokenisation",
      "Reconciliation & fraud monitoring",
    ],
    drivers: ["Financial", "Regulatory"],
  },
  "Payment Card Information": {
    description:
      "Cardholder data captured during sales, top-ups and recurring billing flows.",
    requiredControls: [
      "PCI-DSS scope minimisation & tokenisation",
      "End-to-end encryption",
      "Quarterly ASV scans & annual ROC/SAQ",
      "Segregated cardholder data environment (CDE)",
    ],
    drivers: ["Financial", "Regulatory"],
  },
  "Telecom Network Infrastructure Data": {
    description:
      "Architecture diagrams, element inventories and topology data for RAN, transport, core and edge infrastructure.",
    requiredControls: [
      "Classified document handling",
      "Strict NDA & need-to-know access",
      "Encryption at rest",
      "Watermarking for external sharing",
    ],
    drivers: ["Security", "Operational"],
  },
  "Operational Network Configuration Data": {
    description:
      "Running configurations, golden templates and change records for routers, switches, firewalls and core network functions.",
    requiredControls: [
      "Configuration management & version control",
      "Privileged access management (PAM)",
      "Automated drift detection",
      "Backup & immutable storage",
      "Multi-party approval for production changes",
    ],
    drivers: ["Security", "Operational"],
  },
  "Lawful Intercept / Regulatory Data": {
    description:
      "Lawful interception requests, targets and intercept product handled under regulatory obligation.",
    requiredControls: [
      "Need-to-know compartmentalisation",
      "Dedicated, isolated infrastructure",
      "Tamper-evident audit logging",
      "Cleared personnel & legal oversight",
      "Strict retention & disposal",
    ],
    drivers: ["Regulatory", "Security"],
  },
  "Internal Corporate Data": {
    description:
      "Workforce records, internal communications, strategy material and other day-to-day enterprise data.",
    requiredControls: [
      "DLP on email/endpoints",
      "MFA & conditional access",
      "Data classification labelling",
      "Mobile device management",
    ],
    drivers: ["Operational", "Reputation"],
  },
  "Vendor / Partner Data": {
    description:
      "Contracts, SLAs, integration credentials and exchange data with suppliers, MVNOs, roaming and channel partners.",
    requiredControls: [
      "Third-party risk assessments",
      "Contractual security clauses",
      "Federated identity & scoped API keys",
      "Continuous monitoring of partner connections",
    ],
    drivers: ["Operational", "Regulatory"],
  },
  "Intellectual Property / Proprietary Technology": {
    description:
      "Source code, network designs, patents and other proprietary technology developed or licensed by the organisation.",
    requiredControls: [
      "Source-code repository access controls",
      "Secrets scanning & egress monitoring",
      "IP labelling & DLP",
      "Insider-threat program",
    ],
    drivers: ["Reputation", "Financial"],
  },
};

export function getDataTypeEntry(label: string): DataTypeCatalogEntry {
  return (
    DATA_TYPE_CATALOG[label] ?? {
      description:
        "Sensitive organisational data captured during telecom operations.",
      requiredControls: [
        "Access control & MFA",
        "Encryption at rest and in transit",
        "Audit logging",
        "Backup & recovery",
      ],
      drivers: ["Operational"],
    }
  );
}

const SENS_WEIGHT: Record<string, number> = { Low: 1, Moderate: 2, High: 3, Critical: 4 };
const VOL_WEIGHT: Record<string, number> = { Small: 1, Moderate: 2, Large: 3, "Very Large": 4 };

/**
 * Compose a one-line business impact statement from
 * classification + volume + drivers.
 */
export function composeBusinessImpact(args: {
  sensitivity: string | null;
  volume: string | null;
  drivers?: string[];
}): string {
  const s = (args.sensitivity ?? "").trim();
  const v = (args.volume ?? "").trim();
  const sw = SENS_WEIGHT[s] ?? 0;
  const vw = VOL_WEIGHT[v] ?? 0;
  const drivers = (args.drivers ?? []).filter(Boolean);

  const driverPhrase =
    drivers.length === 0
      ? ""
      : drivers.length === 1
      ? ` ${drivers[0].toLowerCase()} exposure`
      : ` ${drivers.slice(0, -1).map((d) => d.toLowerCase()).join(", ")} and ${drivers[
          drivers.length - 1
        ].toLowerCase()} exposure`;

  // Severity band
  let band: "Severe" | "Significant" | "Moderate" | "Limited";
  const score = sw + vw;
  if (sw >= 4 || score >= 7) band = "Severe";
  else if (sw === 3 || score >= 5) band = "Significant";
  else if (sw === 2 || score >= 3) band = "Moderate";
  else band = "Limited";

  const volPhrase = v ? ` across ${v.toLowerCase()} data volumes` : "";

  switch (band) {
    case "Severe":
      return `Severe impact potential${volPhrase} — loss or compromise would create direct${driverPhrase}, regulatory action and material service disruption.`;
    case "Significant":
      return `Significant impact${volPhrase} — exposure would drive notification obligations,${driverPhrase || " operational"} disruption and customer trust damage.`;
    case "Moderate":
      return `Moderate impact${volPhrase} — exposure would create${driverPhrase || " operational"} disruption and require remediation but is unlikely to be catastrophic.`;
    default:
      return `Limited impact${volPhrase} — controls should remain commensurate with${driverPhrase || " operational"} risk without disproportionate investment.`;
  }
}
